import { useForm } from '../../contexts/FormContext'
import { Button, SaveButton } from '../../components/common'
import { Input, Textarea, Radio, Checkbox, FileUpload } from '../../components/forms'
import './Step3Customization.css'

const STYLE_PREFERENCES = [
  'Minimalist / Clean',
  'Bold / Vibrant',
  'Professional / Corporate',
  'Creative / Artistic',
  'Modern / Tech-forward',
  'Traditional / Classical',
  'Warm / Friendly',
  'Luxurious / Premium'
]

const LOGO_STYLES = [
  'Text-based (wordmark)',
  'Icon + text (combination mark)',
  'Abstract symbol',
  'Illustrative/mascot',
  'Minimal/geometric',
  'No strong preference'
]

const BRAND_MATERIALS = [
  'Brand guidelines document',
  'Previous website screenshots',
  'Marketing materials (brochures, flyers)',
  'Social media graphics',
  'Other materials'
]

const COLOR_MOODS = [
  'Energetic and vibrant',
  'Calm and trustworthy',
  'Bold and confident',
  'Elegant and refined',
  'Fresh and modern',
  'No preference'
]

function Step3Customization() {
  const { formData, updateFormData, updateNestedFormData, goToNextStep, goToPreviousStep, isUploading } = useForm()

  const handleLogoOptionChange = (option) => {
    const logoCost = option === 'need_development' ? 50000 : 0
    const currentLogoCost = formData.logoOption === 'need_development' ? 50000 : 0
    const logoWeeks = option === 'need_development' ? 2 : 0
    const currentLogoWeeks = formData.logoOption === 'need_development' ? 2 : 0

    updateFormData({
      logoOption: option,
      costs: {
        ...formData.costs,
        setupTotal: formData.costs.setupTotal - currentLogoCost + logoCost
      },
      timeline: {
        ...formData.timeline,
        estimatedWeeks: formData.timeline.estimatedWeeks - currentLogoWeeks + logoWeeks
      }
    })
  }

  const handleColorChange = (field, value) => {
    updateNestedFormData(`brandColors.${field}`, value)
  }

  const handleStylePreferenceToggle = (style) => {
    const current = formData.customDesign.stylePreferences
    const updated = current.includes(style)
      ? current.filter(s => s !== style)
      : [...current, style]
    updateNestedFormData('customDesign.stylePreferences', updated)
  }

  const handleLogoStyleToggle = (style) => {
    const current = formData.customDesign.logoStylePreferences
    const updated = current.includes(style)
      ? current.filter(s => s !== style)
      : [...current, style]
    updateNestedFormData('customDesign.logoStylePreferences', updated)
  }

  const handleBrandMaterialToggle = (material) => {
    const current = formData.customDesign.existingBrandMaterials
    const updated = current.includes(material)
      ? current.filter(m => m !== material)
      : [...current, material]
    updateNestedFormData('customDesign.existingBrandMaterials', updated)
  }

  const canProceed = formData.logoOption !== ''

  // Determine if showing template customization or custom design form
  const isTemplateApproach = formData.designApproach === 'template'

  return (
    <div className="step-section">
      {/* Section Header */}
      <div className="section-header">
        <h2>Step 3: {isTemplateApproach ? 'Customize Your Template' : 'Let\'s Design Your Unique Website'}</h2>
        <p className="section-subtitle">
          {isTemplateApproach ? 'Add your branding elements' : 'Tell us about your design vision'}
        </p>
      </div>

      {/* Section Body */}
      <div className="section-body">
        {isTemplateApproach ? (
          /* ===== STEP 3A: TEMPLATE CUSTOMIZATION ===== */
          <div className="customization-form">
            {/* Logo Section */}
            <div className="form-section-block">
              <h3 className="block-title">YOUR LOGO</h3>
              <div className="radio-group">
                <Radio
                  id="logo-have"
                  name="logoOption"
                  value="have_logo"
                  label="I have a logo ready (upload below)"
                  checked={formData.logoOption === 'have_logo'}
                  onChange={(e) => handleLogoOptionChange(e.target.value)}
                />
                {formData.logoOption === 'have_logo' && (
                  <div className="upload-section">
                    <FileUpload
                      id="logo-upload"
                      label="Upload Your Logo"
                      accept=".ai,.eps,.svg,.png,.pdf"
                      multiple={false}
                      uploadType="logo"
                      files={formData.logoFile ? [formData.logoFile] : []}
                      onChange={(files) => updateFormData({ logoFile: files[0] || null })}
                      helperText="AI, EPS, SVG, PNG (transparent), or PDF"
                    />
                  </div>
                )}
                <Radio
                  id="logo-develop"
                  name="logoOption"
                  value="need_development"
                  label="I need logo development (+¥50,000, +2 weeks)"
                  checked={formData.logoOption === 'need_development'}
                  onChange={(e) => handleLogoOptionChange(e.target.value)}
                />
                <Radio
                  id="logo-later"
                  name="logoOption"
                  value="provide_later"
                  label="I'll provide my logo later"
                  checked={formData.logoOption === 'provide_later'}
                  onChange={(e) => handleLogoOptionChange(e.target.value)}
                />
              </div>
            </div>

            {/* Brand Colors Section */}
            <div className="form-section-block">
              <h3 className="block-title">BRAND COLORS (OPTIONAL)</h3>
              <p className="block-description">We'll use template defaults if not provided</p>

              <div className="color-inputs">
                <Input
                  id="primary-color"
                  label="Primary Color"
                  type="color"
                  value={formData.brandColors.primary}
                  onChange={(e) => handleColorChange('primary', e.target.value)}
                  helperText="Main brand color"
                  disabled={formData.brandColors.useTemplateDefaults}
                />
                <Input
                  id="secondary-color"
                  label="Secondary Color"
                  type="color"
                  value={formData.brandColors.secondary}
                  onChange={(e) => handleColorChange('secondary', e.target.value)}
                  helperText="Supporting color"
                  disabled={formData.brandColors.useTemplateDefaults}
                />
                <Input
                  id="accent-color"
                  label="Accent Color"
                  type="color"
                  value={formData.brandColors.accent}
                  onChange={(e) => handleColorChange('accent', e.target.value)}
                  helperText="Highlight color"
                  disabled={formData.brandColors.useTemplateDefaults}
                />
              </div>

              <Checkbox
                id="use-template-colors"
                label="Use template's default color scheme"
                checked={formData.brandColors.useTemplateDefaults}
                onChange={(e) => updateNestedFormData('brandColors.useTemplateDefaults', e.target.checked)}
              />
            </div>

            {/* Design Notes Section */}
            <div className="form-section-block">
              <h3 className="block-title">ADDITIONAL DESIGN NOTES</h3>
              <Textarea
                id="design-notes"
                placeholder="Examples: 'We prefer minimalist, clean designs' or 'Please emphasize our eco-friendly mission' or 'Modern but professional, targeting corporate clients'"
                value={formData.designNotes}
                onChange={(e) => updateFormData({ designNotes: e.target.value })}
                rows={4}
                maxLength={500}
                showCount
                helperText="Any specific design preferences or requirements"
              />
            </div>
          </div>
        ) : (
          /* ===== STEP 3B: CUSTOM DESIGN QUESTIONNAIRE ===== */
          <div className="customization-form">
            {/* Introduction */}
            <div className="intro-text">
              <p><strong>Our design team will create a custom website tailored to your brand.</strong> This process uses Figma for collaborative design reviews. Timeline: 3-4 weeks for design + development.</p>
            </div>

            {/* Question 1: Business & Brand */}
            <div className="form-section-block">
              <h3 className="block-title">TELL US ABOUT YOUR BUSINESS</h3>
              <p className="block-description">What we want to know:</p>
              <ul className="hint-list">
                <li>What does your company do?</li>
                <li>Who are your target customers?</li>
                <li>What makes you different from competitors?</li>
              </ul>
              <Textarea
                id="business-description"
                placeholder="Describe your business, target audience, and unique value proposition..."
                value={formData.customDesign.businessDescription}
                onChange={(e) => updateNestedFormData('customDesign.businessDescription', e.target.value)}
                rows={6}
                maxLength={1000}
                showCount
              />
            </div>

            {/* Question 2: Design Style Preferences */}
            <div className="form-section-block">
              <h3 className="block-title">DESIGN STYLE PREFERENCES</h3>
              <p className="block-description">Select all that apply</p>
              <div className="checkbox-grid">
                {STYLE_PREFERENCES.map(style => (
                  <Checkbox
                    key={style}
                    id={`style-${style.toLowerCase().replace(/[^\w]/g, '-')}`}
                    label={style}
                    checked={formData.customDesign.stylePreferences.includes(style)}
                    onChange={() => handleStylePreferenceToggle(style)}
                  />
                ))}
              </div>
              <Input
                id="other-styles"
                label="Other style keywords"
                placeholder="e.g., futuristic, playful, serious..."
                value={formData.customDesign.otherStyles}
                onChange={(e) => updateNestedFormData('customDesign.otherStyles', e.target.value)}
              />
            </div>

            {/* Question 3: Color Direction */}
            <div className="form-section-block">
              <h3 className="block-title">COLOR PREFERENCES</h3>
              <div className="radio-group">
                <Radio
                  id="color-have"
                  name="colorPreference"
                  value="have_colors"
                  label="I have specific brand colors (provide below)"
                  checked={formData.customDesign.colorPreference === 'have_colors'}
                  onChange={(e) => updateNestedFormData('customDesign.colorPreference', e.target.value)}
                />
                <Radio
                  id="color-recommendations"
                  name="colorPreference"
                  value="need_recommendations"
                  label="I'm open to recommendations"
                  checked={formData.customDesign.colorPreference === 'need_recommendations'}
                  onChange={(e) => updateNestedFormData('customDesign.colorPreference', e.target.value)}
                />
                <Radio
                  id="color-industry"
                  name="colorPreference"
                  value="industry_based"
                  label="Show me options based on my industry"
                  checked={formData.customDesign.colorPreference === 'industry_based'}
                  onChange={(e) => updateNestedFormData('customDesign.colorPreference', e.target.value)}
                />
              </div>

              {formData.customDesign.colorPreference === 'have_colors' && (
                <>
                  <div className="color-inputs">
                    <Input
                      id="custom-primary"
                      label="Primary Color"
                      type="color"
                      value={formData.brandColors.primary}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                    />
                    <Input
                      id="custom-secondary"
                      label="Secondary Color"
                      type="color"
                      value={formData.brandColors.secondary}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                    />
                    <Input
                      id="custom-accent"
                      label="Accent Color"
                      type="color"
                      value={formData.brandColors.accent}
                      onChange={(e) => handleColorChange('accent', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Color Mood</label>
                    <select
                      className="select"
                      value={formData.customDesign.colorMood}
                      onChange={(e) => updateNestedFormData('customDesign.colorMood', e.target.value)}
                    >
                      <option value="">Select a mood...</option>
                      {COLOR_MOODS.map(mood => (
                        <option key={mood} value={mood}>{mood}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            {/* Question 4: Website Inspiration - Simplified */}
            <div className="form-section-block">
              <h3 className="block-title">WEBSITE INSPIRATION</h3>
              <p className="block-description">Provide 1-2 websites you admire (doesn't need to be in your industry)</p>
              <Textarea
                id="inspiration-sites"
                placeholder="Example: https://example.com - I like their clean layout and simple navigation"
                value={formData.customDesign.inspirationSites.join('\n')}
                onChange={(e) => updateNestedFormData('customDesign.inspirationSites', e.target.value.split('\n').filter(s => s.trim()))}
                rows={4}
                helperText="One website per line with a note about what you like"
              />
            </div>

            {/* Question 5: Logo Requirements */}
            <div className="form-section-block">
              <h3 className="block-title">LOGO DEVELOPMENT</h3>
              <div className="radio-group">
                <Radio
                  id="custom-logo-have"
                  name="logoOption"
                  value="have_logo"
                  label="I have an existing logo"
                  checked={formData.logoOption === 'have_logo'}
                  onChange={(e) => handleLogoOptionChange(e.target.value)}
                />
                <Radio
                  id="custom-logo-develop"
                  name="logoOption"
                  value="need_development"
                  label="I need a new logo designed (+¥50,000, +2 weeks)"
                  checked={formData.logoOption === 'need_development'}
                  onChange={(e) => handleLogoOptionChange(e.target.value)}
                />
                <Radio
                  id="custom-logo-separate"
                  name="logoOption"
                  value="provide_later"
                  label="I'll handle logo separately"
                  checked={formData.logoOption === 'provide_later'}
                  onChange={(e) => handleLogoOptionChange(e.target.value)}
                />
              </div>

              {formData.logoOption === 'need_development' && (
                <div className="logo-design-section">
                  <h4 className="subsection-title">LOGO STYLE PREFERENCES</h4>
                  <div className="checkbox-grid">
                    {LOGO_STYLES.map(style => (
                      <Checkbox
                        key={style}
                        id={`logo-style-${style.toLowerCase().replace(/[^\w]/g, '-')}`}
                        label={style}
                        checked={formData.customDesign.logoStylePreferences.includes(style)}
                        onChange={() => handleLogoStyleToggle(style)}
                      />
                    ))}
                  </div>
                  <Textarea
                    id="logo-notes"
                    label="Logo concept notes"
                    placeholder="Describe your logo ideas or preferences..."
                    value={formData.customDesign.logoNotes}
                    onChange={(e) => updateNestedFormData('customDesign.logoNotes', e.target.value)}
                    rows={3}
                    maxLength={500}
                    showCount
                  />
                </div>
              )}
            </div>

            {/* Question 6: Design Assets */}
            <div className="form-section-block">
              <h3 className="block-title">EXISTING BRAND MATERIALS</h3>
              <p className="block-description">Do you have any existing brand materials?</p>
              <div className="checkbox-grid">
                {BRAND_MATERIALS.map(material => (
                  <Checkbox
                    key={material}
                    id={`material-${material.toLowerCase().replace(/[^\w]/g, '-')}`}
                    label={material}
                    checked={formData.customDesign.existingBrandMaterials.includes(material)}
                    onChange={() => handleBrandMaterialToggle(material)}
                  />
                ))}
              </div>
              <div className="upload-section">
                <FileUpload
                  id="brand-materials-upload"
                  label="Upload Brand Materials"
                  accept=".pdf,.doc,.docx,.jpg,.png,.ai,.psd,.eps,.svg"
                  multiple={true}
                  uploadType="brand-materials"
                  files={formData.customDesign.brandMaterialFiles || []}
                  onChange={(files) => updateNestedFormData('customDesign.brandMaterialFiles', files)}
                  helperText="PDF, Word docs, images, or design files"
                />
              </div>
            </div>

            {/* Question 7: Design Review Process */}
            <div className="form-section-block">
              <h3 className="block-title">DESIGN COLLABORATION</h3>
              <div className="info-box">
                <p><strong>We'll use Figma for design reviews.</strong> You'll be able to:</p>
                <ul>
                  <li>View designs in real-time</li>
                  <li>Leave comments and feedback</li>
                  <li>See revisions instantly</li>
                </ul>
                <p><strong>Number of design revision rounds included: 2</strong></p>
                <p className="text-muted">(Additional revisions: ¥10,000/hour)</p>
              </div>

              <h4 className="subsection-title">PREFERRED FEEDBACK METHOD</h4>
              <div className="radio-group">
                <Radio
                  id="feedback-figma"
                  name="feedbackMethod"
                  value="figma"
                  label="Figma comments"
                  checked={formData.customDesign.feedbackMethod === 'figma'}
                  onChange={(e) => updateNestedFormData('customDesign.feedbackMethod', e.target.value)}
                />
                <Radio
                  id="feedback-email"
                  name="feedbackMethod"
                  value="email"
                  label="Email with screenshots"
                  checked={formData.customDesign.feedbackMethod === 'email'}
                  onChange={(e) => updateNestedFormData('customDesign.feedbackMethod', e.target.value)}
                />
                <Radio
                  id="feedback-video"
                  name="feedbackMethod"
                  value="video"
                  label="Video call walkthrough"
                  checked={formData.customDesign.feedbackMethod === 'video'}
                  onChange={(e) => updateNestedFormData('customDesign.feedbackMethod', e.target.value)}
                />
                <Radio
                  id="feedback-all"
                  name="feedbackMethod"
                  value="all"
                  label="All of the above"
                  checked={formData.customDesign.feedbackMethod === 'all'}
                  onChange={(e) => updateNestedFormData('customDesign.feedbackMethod', e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section Actions */}
      <div className="section-actions">
        <Button variant="secondary" size="md" onClick={goToPreviousStep} disabled={isUploading}>
          ← Back
        </Button>
        <div className="actions-right">
          <SaveButton />
          <Button variant="primary" size="md" disabled={!canProceed || isUploading} onClick={goToNextStep}>
            {isUploading ? 'Uploading...' : 'Next Step'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Step3Customization
